// src/pages/Admin/Photos/components/PhotosPreviewModal.jsx
import React, { useEffect } from "react";
import {
  FiExternalLink,
  FiFileText,
  FiImage,
  FiLock,
  FiX,
} from "react-icons/fi";

import {
  formatFileSize,
  isImageMedia,
  isPdfMedia,
  toAbsolutePublicUrl,
} from "../../../../services/mediaLibraryServices";

const formatDateTime = (iso) => {
  if (!iso) return "";

  try {
    const d = new Date(iso);

    if (Number.isNaN(d.getTime())) return "";

    return d.toLocaleString("fr-FR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
};

export default function PhotosPreviewModal({ open, onClose, item }) {
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";

    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  if (!open || !item) return null;

  const url = toAbsolutePublicUrl(item.url);
  const image = isImageMedia(item);
  const pdf = isPdfMedia(item);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const openInNewTab = () => {
    if (!url) return;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/75 p-4 backdrop-blur-sm"
      onClick={handleBackdropClick}
    >
      <div className="flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-900">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4 dark:border-slate-800">
          <div className="min-w-0">
            <div className="mb-1 flex items-center gap-2">
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-xl bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                {image ? (
                  <FiImage className="h-4 w-4" />
                ) : (
                  <FiFileText className="h-4 w-4" />
                )}
              </span>

              <h2 className="truncate text-lg font-bold text-slate-950 dark:text-white">
                {item.title || item.original_name || item.file_name || "Média"}
              </h2>
            </div>

            <p className="text-xs text-slate-500 dark:text-slate-400">
              Prévisualisation et informations du média
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-slate-200 text-slate-500 transition hover:bg-slate-50 hover:text-slate-800 dark:border-slate-700 dark:hover:bg-slate-800 dark:hover:text-white"
          >
            <FiX className="h-4 w-4" />
          </button>
        </div>

        <div className="grid min-h-0 flex-1 grid-cols-1 overflow-y-auto lg:grid-cols-[1.25fr_0.75fr]">
          <div className="flex items-center justify-center bg-slate-100 p-4 dark:bg-slate-950">
            {image ? (
              <div className="flex max-h-[62vh] w-full items-center justify-center overflow-hidden rounded-3xl bg-slate-200 dark:bg-slate-900">
                <img
                  src={url}
                  alt={item.alt_text || item.title || item.file_name}
                  className="max-h-[62vh] w-auto object-contain"
                />
              </div>
            ) : pdf ? (
              <div className="flex w-full max-w-lg flex-col items-center justify-center rounded-[28px] border border-red-100 bg-white p-8 text-center shadow-sm dark:border-red-950/70 dark:bg-slate-900">
                <div className="mb-5 flex h-24 w-24 items-center justify-center rounded-[28px] bg-red-600 text-white shadow-lg shadow-red-600/20">
                  <FiFileText className="h-12 w-12" />
                </div>

                <p className="text-xl font-bold text-slate-950 dark:text-white">
                  Document PDF
                </p>

                <p className="mt-2 max-w-sm text-sm leading-6 text-slate-500 dark:text-slate-400">
                  La prévisualisation intégrée n’est pas affichée ici, mais le
                  document peut être ouvert directement dans un nouvel onglet.
                </p>

                <button
                  type="button"
                  onClick={openInNewTab}
                  className="mt-6 inline-flex items-center justify-center gap-2 rounded-2xl bg-red-600 px-5 py-3 text-sm font-bold text-white shadow-sm transition hover:bg-red-700"
                >
                  <FiExternalLink className="h-4 w-4" />
                  Ouvrir le PDF
                </button>

                <p className="mt-4 break-all text-xs text-slate-400">{url}</p>
              </div>
            ) : (
              <div className="flex w-full max-w-lg flex-col items-center justify-center rounded-[28px] border border-slate-200 bg-white p-8 text-center shadow-sm dark:border-slate-800 dark:bg-slate-900">
                <div className="mb-5 flex h-24 w-24 items-center justify-center rounded-[28px] bg-slate-800 text-white">
                  <FiFileText className="h-12 w-12" />
                </div>

                <p className="text-xl font-bold text-slate-950 dark:text-white">
                  Fichier
                </p>

                <p className="mt-2 max-w-sm text-sm leading-6 text-slate-500 dark:text-slate-400">
                  Ce type de fichier ne dispose pas d’une prévisualisation
                  intégrée.
                </p>

                <button
                  type="button"
                  onClick={openInNewTab}
                  className="mt-6 inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-900 px-5 py-3 text-sm font-bold text-white shadow-sm transition hover:bg-slate-800 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-100"
                >
                  <FiExternalLink className="h-4 w-4" />
                  Ouvrir le fichier
                </button>
              </div>
            )}
          </div>

          <aside className="border-t border-slate-200 p-5 dark:border-slate-800 lg:border-l lg:border-t-0">
            <div className="mb-5 flex flex-wrap gap-2">
              <span className="inline-flex items-center rounded-full bg-sky-100 px-3 py-1 text-xs font-bold text-sky-700 dark:bg-sky-950 dark:text-sky-300">
                {image ? "Image" : pdf ? "PDF" : item.type || "Fichier"}
              </span>

              <span
                className={
                  "inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-bold " +
                  (item.is_public
                    ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300"
                    : "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300")
                }
              >
                {!item.is_public && <FiLock className="h-3 w-3" />}
                {item.is_public ? "Public" : "Privé"}
              </span>
            </div>

            <div className="space-y-4 text-sm">
              <div>
                <p className="mb-1 text-xs font-bold uppercase tracking-wide text-slate-500">
                  Titre
                </p>
                <p className="text-slate-900 dark:text-white">
                  {item.title || <span className="italic text-slate-400">—</span>}
                </p>
              </div>

              <div>
                <p className="mb-1 text-xs font-bold uppercase tracking-wide text-slate-500">
                  Catégorie
                </p>
                <p className="text-slate-900 dark:text-white">
                  {item.category || (
                    <span className="italic text-slate-400">Sans catégorie</span>
                  )}
                </p>
              </div>

              <div>
                <p className="mb-1 text-xs font-bold uppercase tracking-wide text-slate-500">
                  Texte alternatif
                </p>
                <p className="text-slate-900 dark:text-white">
                  {item.alt_text || (
                    <span className="italic text-slate-400">—</span>
                  )}
                </p>
              </div>

              <div>
                <p className="mb-1 text-xs font-bold uppercase tracking-wide text-slate-500">
                  Tags
                </p>
                <p className="text-slate-900 dark:text-white">
                  {item.tags || <span className="italic text-slate-400">—</span>}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-2xl bg-slate-50 p-3 dark:bg-slate-950/70">
                  <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500">
                    Taille
                  </p>
                  <p className="mt-1 font-bold text-slate-900 dark:text-white">
                    {formatFileSize(item.size_bytes)}
                  </p>
                </div>

                <div className="rounded-2xl bg-slate-50 p-3 dark:bg-slate-950/70">
                  <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500">
                    Extension
                  </p>
                  <p className="mt-1 font-bold uppercase text-slate-900 dark:text-white">
                    {item.extension || "—"}
                  </p>
                </div>
              </div>

              {image && (item.width || item.height) && (
                <div className="rounded-2xl bg-slate-50 p-3 dark:bg-slate-950/70">
                  <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500">
                    Dimensions
                  </p>
                  <p className="mt-1 font-bold text-slate-900 dark:text-white">
                    {item.width || "—"} × {item.height || "—"} px
                  </p>
                </div>
              )}

              <div>
                <p className="mb-1 text-xs font-bold uppercase tracking-wide text-slate-500">
                  Créé le
                </p>
                <p className="text-slate-900 dark:text-white">
                  {formatDateTime(item.created_at) || (
                    <span className="italic text-slate-400">—</span>
                  )}
                </p>
              </div>

              <div>
                <p className="mb-1 text-xs font-bold uppercase tracking-wide text-slate-500">
                  Nom du fichier
                </p>
                <p className="break-all text-xs text-slate-900 dark:text-white">
                  {item.original_name || item.file_name}
                </p>
              </div>

              <div>
                <p className="mb-1 text-xs font-bold uppercase tracking-wide text-slate-500">
                  URL publique
                </p>
                <p className="break-all text-xs text-slate-500 dark:text-slate-400">
                  {url}
                </p>
              </div>
            </div>

            <div className="mt-6 flex flex-col gap-2">
              <button
                type="button"
                onClick={openInNewTab}
                className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-slate-900 px-4 py-3 text-sm font-bold text-white transition hover:bg-slate-800 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-100"
              >
                <FiExternalLink className="h-4 w-4" />
                {pdf ? "Ouvrir le PDF" : "Ouvrir dans un nouvel onglet"}
              </button>

              <button
                type="button"
                onClick={onClose}
                className="inline-flex w-full items-center justify-center rounded-2xl border border-slate-300 px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                Fermer
              </button>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}